import { useEffect, useRef, useState } from 'react';

interface AutosaveOptions {
  saveFn: () => Promise<void>;
  trigger: unknown[];
  disabled?: boolean;
  debounceMs?: number;
}

export function useAutosave({
  saveFn,
  trigger,
  disabled,
  debounceMs = 1000,
}: AutosaveOptions) {
  const [initialised, setInitialised] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;
  const autosaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (disabled) return;

    if (!initialised) {
      setInitialised(true);
      return;
    }

    setIsSaving(true);
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(async () => {
      try {
        await saveFnRef.current();
      } catch {
        // silently ignore autosave failures
      } finally {
        setIsSaving(false);
      }
    }, debounceMs);

    return () => {
      if (autosaveRef.current) clearTimeout(autosaveRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...trigger, disabled]);

  return { isSaving };
}
